// 🎯 COMPLETE: DashboardCallLogsTable.tsx - Drop-in Replacement
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Table, Input, Tag, Avatar, message, Button, Select } from 'antd';
import { SearchOutlined, ReloadOutlined, UserOutlined } from '@ant-design/icons';
import { PhoneIncoming, PhoneOutgoing, PhoneMissed, User, Volume2 } from 'lucide-react';
import moment from 'moment';
import debounce from 'lodash/debounce';
import ReactAudioPlayer from 'react-audio-player';
import { useSelector } from 'react-redux';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

// Import API service
import { getCallLogs, CallLog, PaginationQuery } from '../../services/callLogsService';
import logger from '../../utils/logger';

interface DashboardCallLogsTableProps {
  fromDate: string;
  toDate: string;
  selectedUserId?: string;
  selectedTeamId?: string;
  isOrganization?: boolean;
  title?: string;
}

const DashboardCallLogsTable: React.FC<DashboardCallLogsTableProps> = ({
  fromDate,
  toDate,
  selectedUserId,
  selectedTeamId,
  isOrganization,
  title = "Call Logs"
}) => {
  logger.log('📱 DashboardCallLogsTable rendered with props:', {
    fromDate, toDate, selectedUserId, selectedTeamId, isOrganization
  });

  // State management
  const [loading, setLoading] = useState(false);
  const [callLogs, setCallLogs] = useState<CallLog[]>([]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // 🆕 Local user filter state
  const [localSelectedUserId, setLocalSelectedUserId] = useState<string>('');

  // Access Redux store and get existing dashboard users
  const StoreData = useSelector((state: any) => state?.rootReducers);
  const userRole = StoreData?.userData?.userDetails?.role?.name;
  const bucketUrl = StoreData?.userData?.userDetails?.bucketUrl;
  
  // 🎯 FIXED: Use existing dashboard user data instead of mock
  const subordinateData = StoreData?.userData?.userDetails?.subordinateEmployees;
  
  // Get all available users from dashboard data
  const users = useMemo(() => {
    if (userRole === "super_admin" && subordinateData) {
      const reportTo = subordinateData?.reportTo || [];
      const teamMembers = subordinateData?.teamMembers || [];
      const orgMembers = subordinateData?.organizationMember || [];
      
      // Union all users and remove duplicates
      const allUsers = [...reportTo, ...teamMembers, ...orgMembers];
      const uniqueUsers = allUsers.filter((user, index, array) => 
        array.findIndex(u => u._id === user._id) === index
      );
      
      logger.log('👥 Available users for call logs filter:', uniqueUsers.length);
      return uniqueUsers;
    }
    return [];
  }, [subordinateData, userRole]);

  logger.log('🔐 User role check:', userRole);
  logger.log('👥 Users available:', users.length);

  // Get call type details with icons and colors
  const getCallTypeDetails = (type: string) => {
    switch (type.toLowerCase()) {
      case 'incoming':
        return { 
          icon: <PhoneIncoming size={16} color="#28a745" />, 
          color: '#28a745', 
          label: 'Incoming' 
        };
      case 'outgoing':
        return { 
          icon: <PhoneOutgoing size={16} color="#007bff" />, 
          color: '#007bff', 
          label: 'Outgoing' 
        };
      case 'missed':
        return { 
          icon: <PhoneMissed size={16} color="#dc3545" />, 
          color: '#dc3545', 
          label: 'Missed' 
        };
      default:
        return { 
          icon: <PhoneIncoming size={16} color="#6c757d" />, 
          color: '#6c757d', 
          label: 'Unknown' 
        };
    }
  };

  // 🎯 FIXED: Call recordings detection (like ActivityItem)
  const getCallRecordings = (record: CallLog) => {
    if (!record.files || !Array.isArray(record.files) || record.files.length === 0) {
      return [];
    }
    
    return record.files.filter((file: any) => {
      const fileName = file.fileName || file.filePath || '';
      const audioExtensions = ['.mp3', '.wav', '.m4a', '.mpga', '.ogg', '.webm'];
      return audioExtensions.some(ext => fileName.toLowerCase().includes(ext));
    });
  };

  // 🎯 FIXED: Fetch call logs with simple userId filter
  const fetchCallLogs = async (page: number = 1, search: string = '') => {
    logger.log('🔄 fetchCallLogs called:', { 
      page, search, fromDate, toDate, localSelectedUserId
    });

    try {
      setLoading(true);

      // 🎯 Simple query building
      const query: PaginationQuery = {
        page: page,
        perPage: pageSize,
        ...(search.trim() ? { search: search.trim() } : {}),
        ...(fromDate ? { fromDate: fromDate } : {}),
        ...(toDate ? { toDate: toDate } : {}),
        ...(localSelectedUserId ? { userId: localSelectedUserId } : {}), // 🆕 Simple userId filter
        orderBy: 'startedAt',
        isAscending: false
      };

      logger.log('📡 API call with query:', query);
      const response = await getCallLogs(query);
      logger.log('✅ API response:', response);

      setCallLogs(response.data || []);
      setTotal(response.total || 0);
      setCurrentPage(page);

    } catch (error) {
      console.error('❌ API error:', error);
      message.error('Failed to fetch call logs');
      setCallLogs([]);
      setTotal(0);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  };

  // 🎯 FIXED: Pagination handler with proper type
  const handleTableChange = (pagination: TablePaginationConfig) => {
    logger.log('📄 Table pagination changed:', pagination);
    
    const { current = 1, pageSize: newPageSize = 20 } = pagination;
    const shouldResetPage = newPageSize !== pageSize;
    const targetPage = shouldResetPage ? 1 : current;
    
    setCurrentPage(targetPage);
    if (newPageSize !== pageSize) {
      setPageSize(newPageSize);
    }
    fetchCallLogs(targetPage, searchText);
  };

  // 🆕 User filter change handler
  const handleUserChange = (userId: string) => {
    logger.log('👤 User filter changed:', userId);
    setLocalSelectedUserId(userId);
    setCurrentPage(1);
  };

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      logger.log('🔍 Debounced search triggered:', searchTerm);
      setCurrentPage(1);
      fetchCallLogs(1, searchTerm);
    }, 500),
    [localSelectedUserId, fromDate, toDate, pageSize]
  );

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    logger.log('🔍 Search input changed:', value);
    setSearchText(value);
    
    debouncedSearch.cancel();
    if (value.trim()) {
      setCurrentPage(1);
      debouncedSearch(value.trim());
    } else {
      setCurrentPage(1);
      fetchCallLogs(1, '');
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    logger.log('🔄 Refresh button clicked');
    debouncedSearch.cancel();
    setSearchText('');
    setCurrentPage(1);
    fetchCallLogs(1, '');
  };

  // 🎯 FIXED: Pagination config
  const paginationConfig: TablePaginationConfig = useMemo(() => ({
    current: currentPage,
    pageSize: pageSize,
    total: total,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} calls`,
    pageSizeOptions: ['10', '20', '50', '100'],
    position: ['bottomCenter'] as ['bottomCenter']
  }), [currentPage, pageSize, total]);

  // Sync selectedUserId prop with local state
  useEffect(() => {
    if (selectedUserId !== undefined) {
      setLocalSelectedUserId(selectedUserId);
    }
  }, [selectedUserId]);

  // Load data when filters change
  useEffect(() => {
    if (userRole === "super_admin") {
      logger.log('🔄 Filters changed, fetching call logs');
      setSearchText('');
      setCurrentPage(1);
      fetchCallLogs(1, '');
      debouncedSearch.cancel();
    }
  }, [fromDate, toDate, localSelectedUserId, pageSize]);

  // Table columns definition
  const columns: ColumnsType<CallLog> = [
    {
      title: 'Contact',
      key: 'contact',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar 
            icon={<User size={20} />} 
            style={{ backgroundColor: '#1890ff', flexShrink: 0 }} 
          />
          <div style={{ minWidth: 0 }}>
            <div style={{ 
              fontWeight: 600, 
              fontSize: '14px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {record.lead?.name || record.userContact?.firstName || 'Unknown'}
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#666',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {record.phone}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      width: 100,
      render: (_, record) => {
        const callType = getCallTypeDetails(record.type);
        return (
          <Tag 
            color={callType.color} 
            style={{ 
              fontSize: '11px', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              width: 'fit-content'
            }}
          >
            {callType.icon} {callType.label}
          </Tag>
        );
      },
    },
    {
      title: 'Date & Time',
      key: 'datetime',
      width: 140,
      sorter: true,
      render: (_, record) => (
        <div style={{ fontSize: '12px', color: '#333' }}>
          {moment(record.startedAt).format('MMM Do')}
          <br />
          <span style={{ color: '#666' }}>
            {moment(record.startedAt).format('h:mm A')}
          </span>
        </div>
      ),
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 80,
      render: (_, record) => (
        <div style={{ 
          fontSize: '12px', 
          fontWeight: 500, 
          color: '#333',
          fontFamily: 'monospace'
        }}>
          {record.duration ? moment.utc(record.duration * 1000).format('mm:ss') : '00:00'}
        </div>
      ),
    },
    {
      title: 'Details',
      key: 'details',
      render: (_, record) => {
        const callRecordings = getCallRecordings(record);
        const hasRecording = callRecordings.length > 0;
        
        return (
          <div style={{ minWidth: 200 }}>
            {/* Call metadata */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: hasRecording ? '8px' : '0' }}>
              {record.concurrentCall && record.concurrentCall > 1 && (
                <Tag color="blue" style={{ fontSize: '11px' }}>
                  {record.concurrentCall} Calls
                </Tag>
              )}
              {hasRecording && (
                <Tag 
                  color="green" 
                  style={{ 
                    fontSize: '11px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px' 
                  }}
                >
                  <Volume2 size={12} />
                  Recording
                </Tag>
              )}
            </div>
            
            {/* 🎯 FIXED: Audio recording player (like ActivityItem) */}
            {hasRecording && (
              <div style={{
                background: '#f1f5f9',
                borderRadius: '8px',
                padding: '8px',
                border: '1px solid #e2e8f0'
              }}>
                {callRecordings.map((recording: any, index: number) => (
                  <div key={index}>
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      marginBottom: '6px'
                    }}>
                      <Volume2 size={12} color="#475569" />
                      <span style={{ 
                        fontSize: '11px', 
                        color: '#475569', 
                        fontWeight: 500 
                      }}>
                        Call Recording
                      </span>
                    </div>
                    <ReactAudioPlayer
                      src={`${bucketUrl}${recording.filePath || recording}`}
                      controls
                      className="lead-audio-player"
                      preload="metadata"
                      title="Call Recording"
                      onError={(e) => {
                        console.error('🎵 Audio playback error:', e);
                        message.error('Failed to load call recording');
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      },
    }
  ];

  // Role-based access control - Only super_admin can see call logs
  if (userRole !== "super_admin") {
    logger.log('🚫 Access denied: User is not super_admin');
    return null;
  }

  return (
    <Card style={{ borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      <CardHeader style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', borderRadius: '12px 12px 0 0' }}>
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <div className="fw-bold text-dark d-flex flex-column align-items-start fm-gilroy">
              <span style={{ fontSize: '18px' }}>{title}</span>
              <small style={{ fontSize: '12px', color: '#666', fontWeight: 'normal' }}>
                Super Admin Access Only
                {localSelectedUserId && ` • Filtered by: ${users.find(u => u._id === localSelectedUserId)?.firstName || 'User'}`}
              </small>
            </div>
          </div>
          
          {/* 🆕 Enhanced controls */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            
            {/* 🆕 User Filter Dropdown */}
            <Select
              value={localSelectedUserId}
              onChange={handleUserChange}
              placeholder="All Users"
              style={{ width: 180 }}
              size="middle"
              allowClear
            >
              {users.map(user => (
                <Select.Option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName || ''}
                </Select.Option>
              ))}
            </Select>

            {/* Search input */}
            <Input
              placeholder="Search by name or phone"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={handleSearch}
              allowClear
              style={{ width: 250 }}
              disabled={loading}
            />
            
            {/* Refresh button */}
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              type="default"
            >
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {/* 🎯 FIXED: Remove p-0 to show pagination */}
      <CardBody style={{ padding: '0 0 16px 0' }}>
        <Table
          columns={columns}
          dataSource={callLogs}
          rowKey="_id"
          loading={loading}
          pagination={paginationConfig}
          onChange={handleTableChange}
          scroll={{ x: 800, y: 600 }}
          size="small"
          style={{ background: 'white' }}
          locale={{
            emptyText: searchText ? 'No calls found for your search' : 'No call logs available'
          }}
        />
      </CardBody>
    </Card>
  );
};

export default DashboardCallLogsTable;